import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, TrendingUp, X, ArrowLeft } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { mapPostRow } from '@/lib/mapPost'
import PostCard from '@/components/features/feed/PostCard'
import { STRIPE, type PostData } from '@/data/mockData'

/** 0 = nombre exacto, 1 = empieza por la búsqueda, 2 = la contiene. */
function bizRank(name: string, q: string): number {
  const n = name.toLowerCase()
  if (n === q) return 0
  if (n.startsWith(q)) return 1
  return 2
}

const SUGGESTIONS = [
  'Rooftops',
  'Música en vivo',
  'Brunch',
  '2x1',
  'Casco Antiguo',
  'Eventos gratis',
  'Planes para hoy',
  'Arte',
]
const C = {
  teal: '#0F5C5C',
  terra: '#D9785B',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  grayBg: '#F1F3F2',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

export default function SearchPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [q, setQ] = useState(params.get('q') ?? '')

  // Datos reales de Supabase (antes filtraba sobre datos de ejemplo)
  const { data } = useQuery({
    queryKey: ['search-data'],
    queryFn: async () => {
      if (!supabase) return { posts: [], businesses: [] }
      const [{ data: businesses }, { data: posts }] = await Promise.all([
        supabase
          .from('businesses')
          .select('id, name, category, zone, verified, cover_colors, emoji'),
        supabase
          .from('posts')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false }),
      ])
      return { posts: posts ?? [], businesses: businesses ?? [] }
    },
    staleTime: 1000 * 60,
  })

  const feed: PostData[] = data
    ? data.posts.map((p) =>
        mapPostRow(
          p,
          data.businesses.find((b) => b.id === p.business_id)
        )
      )
    : []

  const query = q.trim().toLowerCase()

  // Negocios que coinciden por nombre/categoría/zona; nombre exacto va primero
  const businessResults = query
    ? (data?.businesses ?? [])
        .filter((b) => `${b.name} ${b.category ?? ''} ${b.zone}`.toLowerCase().includes(query))
        .sort((a, b) => bizRank(a.name, query) - bizRank(b.name, query))
    : []

  const results = query
    ? feed.filter((p) =>
        `${p.title} ${p.desc} ${p.bizName ?? ''} ${p.bizZone ?? ''}`.toLowerCase().includes(query)
      )
    : []

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 28px 60px' }}>
      {/* Barra */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            width: 38,
            height: 38,
            borderRadius: 11,
            border: `1px solid ${C.border}`,
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={18} />
        </button>
        <div
          style={{
            flex: 1,
            maxWidth: 560,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 11,
            padding: '10px 14px',
            boxShadow: '0 2px 8px rgba(0,0,0,.06)',
          }}
        >
          <Search size={17} color={C.ink2} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Busca lugares, promociones, eventos…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontFamily: FB,
              fontSize: 15,
              color: C.ink,
            }}
          />
          {q && (
            <button
              onClick={() => setQ('')}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
            >
              <X size={17} color={C.ink2} />
            </button>
          )}
        </div>
      </div>

      {!q ? (
        <div>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
            Búsquedas populares
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setQ(s)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  background: '#fff',
                  border: `1px solid ${C.border}`,
                  borderRadius: 99,
                  padding: '9px 16px',
                  cursor: 'pointer',
                  fontFamily: FB,
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                <TrendingUp size={14} color={C.terra} /> {s}
              </button>
            ))}
          </div>
        </div>
      ) : businessResults.length === 0 && results.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '70px 0' }}>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
            Sin resultados
          </div>
          <div style={{ fontSize: 14, color: C.ink2 }}>No encontramos nada para "{q}".</div>
        </div>
      ) : (
        <div>
          {/* Negocios */}
          {businessResults.length > 0 && (
            <div style={{ marginBottom: 30 }}>
              <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
                Negocios ({businessResults.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {businessResults.map((b) => {
                  const cover = (b.cover_colors as [string, string]) ?? ['#0F5C5C', '#17706f']
                  return (
                    <button
                      key={b.id}
                      onClick={() => navigate(`/biz/${b.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        background: '#fff',
                        border: `1px solid ${C.border}`,
                        borderRadius: 14,
                        padding: '12px 16px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 13,
                          background: STRIPE(cover[0], cover[1]),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 22,
                          flexShrink: 0,
                        }}
                      >
                        {b.emoji}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span
                            style={{
                              fontFamily: FH,
                              fontWeight: 700,
                              fontSize: 15,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {b.name}
                          </span>
                          {b.verified && <span style={{ color: C.teal, fontSize: 13 }}>✓</span>}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: C.ink2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {b.category} · {b.zone}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Publicaciones */}
          {results.length > 0 && (
            <div>
              <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
                Publicaciones ({results.length})
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill,minmax(360px,1fr))',
                  gap: 16,
                }}
              >
                {results.map((p) => (
                  <PostCard key={p.id} post={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
